import { assertNotEmpty, notEmpty, type Optional } from '@festina-life/core'
import { useCallback } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import {
  ConfigurationFieldType,
  isConfigurationValueSetupField,
  isSimpleValueSetupField,
  isSubTemplatedField,
  isSubTemplateValueSetupField,
  isTemplateReferenceValueSetupField,
  type SubTemplateValueSetupField,
  type TemplateField,
  type ValueSetupFieldValue,
  ValueSetupRole,
} from '../../types/templateModel'
import { isInherited } from '../../uiModels/templateOperations/types'
import { stringifyComparableValue } from '../diffIndicators/helpers'
import type { SerialiseValueParams } from '../diffIndicators/ValueSetupDiffIndicators'
import {
  mergeInheritedArrayValues,
  mergeInheritedNullProps,
  processSubTemplatedFieldValueForSerialisation,
} from './fieldsHelpers'

const TemplateFieldValueEditableProps = [
  'required',
  'requiredInModelInstance',
  'valueConfig',
] as const
const SimpleTemplateFieldValueEditableProps = [
  ...TemplateFieldValueEditableProps,
  'defaultValue',
  'frame',
] as const
const SubTemplateFieldValueEditableProps = [
  ...TemplateFieldValueEditableProps,
  'cardinality',
] as const

export const useSerialisedFieldValue = ({
  field,
}: {
  field: TemplateField
}) => {
  const { templateVersion, isMappingsTemplate } = useTemplateContext()

  assertNotEmpty(templateVersion)

  return useCallback(
    ({ value: comparable, valueType }: SerialiseValueParams<TemplateField>) => {
      if (!comparable?.value) {
        return ''
      }

      let inheritanceCompliantValue = comparable.value

      if (
        valueType === 'inherited' &&
        comparable.value &&
        comparable.fieldType !==
          ConfigurationFieldType.ConfigurationExpressionValue
      ) {
        if (isSubTemplateValueSetupField(comparable)) {
          inheritanceCompliantValue = mergeInheritedNullProps({
            inheritedValue: comparable.value,
            currentValue: field.value as typeof comparable.value,
            keys: [...SubTemplateFieldValueEditableProps],
            currentValuePath: field.value?.path,
          })
        } else if (isSimpleValueSetupField(comparable)) {
          inheritanceCompliantValue = mergeInheritedNullProps({
            inheritedValue: comparable.value,
            currentValue: field.value as typeof comparable.value,
            keys: [...SimpleTemplateFieldValueEditableProps],
            currentValuePath: field.value?.path,
          })
        } else {
          inheritanceCompliantValue = mergeInheritedNullProps({
            inheritedValue: comparable.value,
            currentValue: field.value as typeof comparable.value,
            keys: [...TemplateFieldValueEditableProps],
            currentValuePath: field.value?.path,
          })
        }

        if (isTemplateReferenceValueSetupField(comparable)) {
          const inheritedTemplateReferenceVariants = mergeInheritedArrayValues({
            inheritedValue: comparable.value.variants,
            currentValue:
              (field.value as typeof comparable.value)?.variants ?? [],
            keyFn: (variant) =>
              (isMappingsTemplate ? variant.key : variant.source) ?? '',
          })

          inheritanceCompliantValue = {
            ...inheritanceCompliantValue,
            variants: inheritedTemplateReferenceVariants,
          }
        }

        inheritanceCompliantValue =
          isConfigurationValueSetupField(field) &&
          isConfigurationValueSetupField(comparable)
            ? inheritanceCompliantValue
            : {
                ...inheritanceCompliantValue,
                // Validations are not diffed against in inherited values since they're always completely merged on flattening
                validations:
                  (
                    field.value as Optional<ValueSetupFieldValue>
                  )?.validations?.filter(
                    (validation) => validation.origin === validation.path,
                  ) ?? [],
              }
      }

      if (isSubTemplatedField(comparable)) {
        const { isRenderingEmpty, ...comparableContent } = comparable

        if (comparable.role === ValueSetupRole.SINGLE_CLASS_SUB_TEMPLATE) {
          const typedContent = comparableContent as SubTemplateValueSetupField

          const contentWithoutFieldsAndFunctions = {
            ...typedContent,
            value: {
              ...inheritanceCompliantValue,
              variants:
                typedContent.value?.variants
                  ?.map((variant) => {
                    const currentValue = (
                      field as SubTemplateValueSetupField
                    ).value?.variants.find(
                      (currentValueVariant) =>
                        currentValueVariant.subTemplateIdentifier ===
                        variant.subTemplateIdentifier,
                    )

                    return processSubTemplatedFieldValueForSerialisation({
                      value: variant,
                      valueType,
                      currentValue,
                      currentValuePath: currentValue?.path,
                    })
                  })
                  .filter(
                    (filteredVariant) =>
                      notEmpty(filteredVariant) &&
                      !!Object.keys(filteredVariant).length,
                  ) ?? [],
            },
          }

          return stringifyComparableValue(contentWithoutFieldsAndFunctions)
        } else {
          const variantNames = new Set(
            comparableContent.value?.variants
              .filter((variant) => !isInherited(variant))
              .map((variant) => variant?.subTemplateIdentifier)
              .filter(notEmpty),
          )
          const currentFieldVariantNames = new Set(
            (field as SubTemplateValueSetupField).value?.variants
              .filter((variant) => !isInherited(variant))
              .map((variant) => variant?.subTemplateIdentifier)
              .filter(notEmpty),
          )

          // In order to force a diff between current and previous values, if any variants have been added or removed we set the variant names on the stringified values
          // so that a diff is shown agains the current value.
          const diffedVariantNames =
            valueType === 'previous'
              ? [
                  ...Array.from(
                    variantNames.difference(currentFieldVariantNames),
                  ),
                  ...Array.from(
                    currentFieldVariantNames.difference(variantNames),
                  ),
                ].sort()
              : []

          const comparableContentWithoutVariants = {
            ...comparableContent,
            ...(comparableContent.value
              ? {
                  value: {
                    ...inheritanceCompliantValue,
                    variants: diffedVariantNames ?? [],
                  },
                }
              : {}),
          }

          return stringifyComparableValue(comparableContentWithoutVariants)
        }
      }

      return stringifyComparableValue(inheritanceCompliantValue)
    },
    [field, isMappingsTemplate],
  )
}
